import React, { useMemo, useState } from 'react';

import { DownloadIcon } from '@cloudquery/base-icons';
import { useBaseAuthenticated } from '@cloudquery/base-users';
import Box from '@mui/material/Box';

import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { GridPaginationModel } from '@mui/x-data-grid';

import { GridApiCommunity } from '@mui/x-data-grid/internals';

import { getEnv, MissingPermissionTooltip } from 'shared/base';

import { TableDataExportDialog } from '@/components/tableDataExportDialog';

import { useTableStateContext } from '../../../hooks/useTableStateContext';
import { recordCount } from '../../../utils/table';

import { ColumnToggle } from '../columnToggle';
import { SearchInput } from '../searchInput';

interface Props {
  enableWriteActions: boolean;
  gridApiRef: React.RefObject<GridApiCommunity | null>;
  onSaveCurrentFilter: () => void;
  paginationModel: GridPaginationModel;
  rowCount: number;
  teamName: string;
}

export function CustomToolbar({
  enableWriteActions,
  gridApiRef,
  onSaveCurrentFilter,
  paginationModel,
  rowCount,
  teamName,
}: Props) {
  const [showExportDialog, setShowExportDialog] = useState(false);
  const {
    apiFilter,
    apiSortProps,
    displayColumns,
    filter,
    overwriteDisplayColumns,
    resetDisplayColumns,
    setDisplayColumns,
    tableName,
  } = useTableStateContext();
  const { end, start } = useMemo(() => {
    const pageIndex = Number(paginationModel.page);
    const pageSize = Number(paginationModel.pageSize);

    const start = pageIndex * pageSize;
    const end = start + pageSize;

    return {
      end,
      start: start + 1,
    };
  }, [paginationModel]);

  const { currentMembership } = useBaseAuthenticated();

  return (
    <Stack gap={1} mb={0.5} width="100%">
      <Box display="flex" gap={1.5} width="100%">
        <SearchInput teamName={teamName} />
        <MissingPermissionTooltip hide={enableWriteActions} wrapChildren={!enableWriteActions}>
          <Button
            disabled={!filter || filter.length === 0 || !enableWriteActions}
            onClick={onSaveCurrentFilter}
            size="small"
            sx={{ flex: 'none', height: '42px', marginBottom: 'auto', whiteSpace: 'nowrap' }}
            variant="outlined"
          >
            Save filter
          </Button>
        </MissingPermissionTooltip>
        <TableDataExportDialog
          fetchOptions={{
            baseUrl: `${getEnv(
              'NEXT_PUBLIC_CLOUDQUERY_API_BASE_URL',
              process.env.NEXT_PUBLIC_CLOUDQUERY_API_BASE_URL,
              '',
            )}/teams/${currentMembership.team.name}/tables/${tableName}/data`,
            method: 'GET',
            searchParams: {
              ...apiFilter,
              ...apiSortProps,
            },
          }}
          onClose={() => setShowExportDialog(false)}
          open={showExportDialog}
          page={paginationModel.page + 1}
          perPage={paginationModel.pageSize}
        />
      </Box>

      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
          padding: 0,
        }}
      >
        <Box>
          <Typography color="textSecondary" sx={{ ml: 2 }} variant="caption">
            <>
              Showing{' '}
              {rowCount === 0 ? (
                <>
                  <b>0</b> records
                </>
              ) : (
                <>
                  <b>
                    {recordCount(start)}-{recordCount(Math.min(rowCount, end))}
                  </b>{' '}
                  of <b>{recordCount(rowCount)}</b> records
                </>
              )}
            </>
          </Typography>
        </Box>

        <Box>
          <ColumnToggle
            columns={gridApiRef.current?.getAllColumns?.() ?? []}
            displayColumns={displayColumns}
            overwriteDisplayColumns={overwriteDisplayColumns}
            resetDisplayColumns={resetDisplayColumns}
            setDisplayColumns={setDisplayColumns}
          />
          <Button
            color="secondary"
            endIcon={<DownloadIcon />}
            onClick={() => setShowExportDialog(true)}
            size="small"
            variant="outlined"
          >
            Export
          </Button>
        </Box>
      </Box>
    </Stack>
  );
}
